import type { VariantProps } from "@-/com.npmjs.cva";
import { Button, type ButtonProps } from "@-/dev.improve.haniverse.components.ui.button";
import { Icon, type IconName } from "@-/dev.improve.haniverse.components.ui.icon";
import { iconButtonVariants } from "#icon-button-variants";
import { cn } from "@-/com.npmjs.cn";
import { ActivityIndicator, Text } from "@-/com.npmjs.react-native";
import { useContext } from "react";
import { IconButtonHeightContext } from "#icon-button-height-context";

export type IconButtonProps = Omit<ButtonProps, "children" | "size" | "variant"> &
  VariantProps<typeof iconButtonVariants> & {
    /** Custom content in place of the `name` glyph (e.g. a CJK text label). Wins over `name`. */
    children?: React.ReactNode;
    /**
     * Icon glyph color. Defaults to the theme `foreground` ink (adapts to light/dark) used across
     * the floating toolbars. Pass an explicit color to opt out of the theme-aware default.
     */
    iconColor?: string;
    iconSize?: number;
    /** Replaces the glyph with the standard small, animated loading indicator. */
    isLoading?: boolean;
    /**
     * Optional visible text beside the glyph. It turns the circle into an icon-and-label pill —
     * the shape the toolbar expansion menus use so each revealed action names itself instead of
     * relying on an icon the reader has to decode.
     */
    label?: string;
    /** Icon glyph to render. Omit when passing custom `children`. */
    name?: IconName;
  };

const DEFAULT_ICON_SIZE = 15;

/**
 * Small opaque floating circle button holding a single icon — the always-circular controls on the
 * right of the Browse toolbars (filter / sort / view-mode / return-to-current-level) and the
 * nav-header expansion push buttons. The opaque surface + border keeps content scrolling underneath
 * from bleeding through.
 *
 * Set `active` to paint the pushed-in (toggled-on) state. Pass `children` for a non-icon label (the
 * simplified/traditional glyph); otherwise pass `name`.
 *
 * Extra `Button` props are forwarded so this can be used as a `DropdownMenuTrigger asChild` child
 * (the trigger injects `onPress`/ref/accessibility onto it).
 */
export function IconButton({
  accessibilityLabel,
  active,
  children,
  className,
  iconColor,
  iconSize = DEFAULT_ICON_SIZE,
  isLoading = false,
  label,
  name,
  style,
  ...props
}: IconButtonProps) {
  const height = useContext(IconButtonHeightContext);
  const controlStyle =
    height === undefined
      ? undefined
      : { height, ...(label === undefined ? { width: height } : {}) };
  return (
    <Button
      accessibilityLabel={accessibilityLabel ?? label}
      className={cn(
        iconButtonVariants({ active }),
        // A label makes the control as wide as its text, so the fixed circle size gives way to an
        // auto-width pill. Toolbar buttons override this base height with their responsive metrics.
        label === undefined ? undefined : "h-7 w-auto gap-1.5 px-2.5",
        className,
      )}
      // The fill variant's `p-0` overrides horizontal padding on native.
      size={label === undefined ? "fill" : "xs"}
      variant="outline"
      {...props}
      style={
        controlStyle === undefined
          ? style
          : typeof style === "function"
            ? (state) => [style(state), controlStyle]
            : [style, controlStyle]
      }
    >
      {isLoading ? (
        <ActivityIndicator size="small" />
      ) : (
        // Default to the theme `foreground` ink so the glyph adapts to light/dark; `iconColor` (when
        // provided) wins via `Icon`'s `color` prop taking precedence over the resolved className.
        (children ??
        (name === undefined ? null : (
          <Icon
            // Beside a label the glyph is supporting detail, so it takes the same muted ink as the
            // text; alone in a circle it IS the control and keeps the full-contrast `foreground`.
            className={label === undefined ? "text-foreground" : "text-muted-foreground"}
            color={iconColor}
            name={name}
            size={iconSize}
          />
        )))
      )}
      {label === undefined ? null : (
        <Text className="text-[11px] text-muted-foreground">{label}</Text>
      )}
    </Button>
  );
}
